'use client';

import * as Headless from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { RefObject, useContext, useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import LogoFullWhiteImgSrc from '../../../assets/images/sendit-logo-full-white.svg';
import LogoMarkWhiteImgSrc from '../../../assets/images/sendit-logo-mark-white.svg';
import { UserDetail, NotificationsService } from '../../../js/api';
import { NavBarPlacementRequestsBadge } from '../../../js/app/components/NotificationBadge';
import { SessionContext } from '../../../js/app/contexts/SessionContext';
import { MENU_BADGES_QUERY_KEY } from '../constants/queryKeys';
import { getHomeUrl } from '../utils/navigation';
import { getLocalStorageSafe } from '../utils/storage';
import { TRANSITION_ALL } from '../utils/transitionStyles';
import { getInitials } from '../utils/utils';
import { Avatar } from './avatar';
import { TouchTarget } from './button';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from './dropdown';
import {
  Sidebar,
  SidebarBody,
  SidebarHeader,
  SidebarHeading,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from './sidebar';

// Simple badge component for menu notifications
function MenuBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="tw:ml-2 tw:inline-flex tw:items-center tw:justify-center tw:rounded-full tw:bg-brand-green-1 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-white tw:min-w-[1.25rem]">
      {count}
    </span>
  );
}

// Hook to fetch menu badge counts
function useMenuBadges(user: UserDetail | undefined) {
  // Only fetch badges if user is logged in
  const shouldFetch = !!user;

  const { data: menuCounts = {} } = useQuery({
    queryKey: [MENU_BADGES_QUERY_KEY],
    queryFn: async () => {
      const response = await NotificationsService.notificationsBadgesMenuRetrieve({
        scope: undefined,
      });

      return (response as Record<string, { alert_count: number }>) || {};
    },
    enabled: shouldFetch,
    staleTime: 30 * 1000, // 30 seconds - notifications may change frequently
  });

  return menuCounts;
}

// Adapter component to make SidebarItem work with React Router
// This replicates SidebarItem's structure but uses React Router's Link
function RouterSidebarItem({
  to,
  current,
  children,
  onClick,
  className,
  collapsed,
}: {
  to: string;
  current?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  collapsed?: boolean;
}) {
  const classes = clsx(
    // Base
    'tw:flex tw:w-full tw:items-center tw:gap-1 tw:rounded-lg tw:px-2 tw:py-2.5 tw:text-left tw:text-base/6 tw:font-medium tw:!text-white tw:sm:py-2 tw:sm:text-sm/5 tw:!no-underline',
    // Leading icon/icon-only
    'tw:data-[slot=icon]:*:tw:size-6 data-[slot=icon]:*:tw:shrink-0 tw:data-[slot=icon]:*:tw:fill-zinc-500 tw:sm:data-[slot=icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'tw:data-[slot=icon]:last:*:tw:ml-auto data-[slot=icon]:last:*:tw:size-5 tw:sm:data-[slot=icon]:last:*:size-4',
    // Avatar
    'tw:data-[slot=avatar]:*:tw:-m-0.5 data-[slot=avatar]:*:tw:size-7 tw:data-[slot=avatar]:*:[--ring-opacity:10%] tw:sm:data-[slot=avatar]:*:size-6',
    // Hover
    'tw:data-[hover]:bg-brand-green-1 tw:data-[hover]:!text-white  data-[slot=icon]:*:tw:data-[hover]:fill-zinc-950',
    // Active
    'tw:data-[active]:bg-zinc-950/5 data-[slot=icon]:*:tw:data-[active]:fill-zinc-950',
    // Current
    'tw:data-[slot=icon]:*:tw:data-[current]:fill-zinc-950',
    'tw:data-[current]:bg-brand-green-2 tw:data-[current]:!text-brand-green-0',
    // Collapsed state - center icons
    collapsed && 'tw:justify-center tw:px-2 tw:h-12'
  );

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <span className={clsx(className, 'tw:relative')}>
      <Headless.DataInteractive>
        <RouterLink
          to={to}
          onClick={handleClick}
          className={classes}
          data-current={current ? 'true' : undefined}
        >
          <TouchTarget>{children}</TouchTarget>
        </RouterLink>
      </Headless.DataInteractive>
    </span>
  );
}

// Helper function to get provider sidebar items
function getProviderSidebarItems(
  isCurrentRoute: (path: string) => boolean,
  menuCounts: Record<string, { alert_count: number }>,
  collapsed?: boolean,
  onClick?: () => void
): React.ReactElement[] {
  const connectionsCount = menuCounts['Connections']?.alert_count || 0;

  return [
    <RouterSidebarItem
      key="profile"
      to="/app/provider/profile"
      current={isCurrentRoute('/app/provider/profile')}
      collapsed={collapsed}
      onClick={onClick}
    >
      <i className={clsx('fal fa-fw fa-lg fa-address-card', !collapsed && 'tw:mr-1')} />
      {!collapsed && <SidebarLabel>Profile</SidebarLabel>}
    </RouterSidebarItem>,
    <RouterSidebarItem
      key="connections"
      to="/app/provider/connections"
      current={isCurrentRoute('/app/provider/connections')}
      collapsed={collapsed}
      onClick={onClick}
    >
      <i className={clsx('fal fa-fw fa-lg fa-handshake', !collapsed && 'tw:mr-1')} />
      {!collapsed && <SidebarLabel>Connections</SidebarLabel>}
      {!collapsed && connectionsCount > 0 && <MenuBadge count={connectionsCount} />}
    </RouterSidebarItem>,
    <RouterSidebarItem
      key="assignments"
      to="/app/provider/booking-requests"
      current={isCurrentRoute('/app/provider/booking-requests')}
      collapsed={collapsed}
      onClick={onClick}
    >
      <i className={clsx('fal fa-fw fa-lg fa-calendar-lines-pen', !collapsed && 'tw:mr-1')} />
      {!collapsed && <SidebarLabel>Assignments</SidebarLabel>}
    </RouterSidebarItem>,
  ];
}

// Helper function to get HCO sidebar items
function getOrganizationSidebarItems(
  user: UserDetail | undefined,
  isCurrentRoute: (path: string) => boolean,
  menuCounts: Record<string, { alert_count: number }>,
  collapsed?: boolean,
  onClick?: () => void
): React.ReactElement[] {
  const items: React.ReactElement[] = [];

  const clearancesCount = menuCounts['Clearances']?.alert_count || 0;
  const opportunitiesCount = menuCounts['Opportunities']?.alert_count || 0;
  const connectionsCount = menuCounts['Connections']?.alert_count || 0;

  // Providers - always shown
  if (user?.permissions?.includes('list__providers__provider')) {
    items.push(
      <RouterSidebarItem
        key="providers"
        to="/app/healthcare-entity/providers"
        current={isCurrentRoute('/app/healthcare-entity/providers')}
        collapsed={collapsed}
        onClick={onClick}
      >
        <i className={clsx('fal fa-fw fa-lg fa-stethoscope', !collapsed && 'tw:mr-1')} />
        {!collapsed && <SidebarLabel>Providers</SidebarLabel>}
      </RouterSidebarItem>
    );
  }
  // Staffing - Needs (conditional on permission)
  if (user?.permissions?.includes('list__staffing__need')) {
    items.push(
      <RouterSidebarItem
        key="needs"
        to="/app/organization/needs"
        current={isCurrentRoute('/app/organization/needs')}
        collapsed={collapsed}
        onClick={onClick}
      >
        <i className={clsx('far fa-fw fa-lg fa-users-medical', !collapsed && 'tw:mr-1')} />
        {!collapsed && <SidebarLabel>Needs</SidebarLabel>}
      </RouterSidebarItem>
    );
  }

  // Staffing - Opportunities (conditional on permission)
  if (user?.permissions?.includes('list__assignments__opportunity')) {
    items.push(
      <RouterSidebarItem
        key="opportunities"
        to="/app/organization/opportunities"
        current={isCurrentRoute('/app/organization/opportunities')}
        collapsed={collapsed}
        onClick={onClick}
      >
        <i className={clsx('fal fa-fw fa-lg fa-suitcase-medical', !collapsed && 'tw:mr-1')} />
        {!collapsed && <SidebarLabel>Opportunities</SidebarLabel>}
        {!collapsed && opportunitiesCount > 0 && <MenuBadge count={opportunitiesCount} />}
      </RouterSidebarItem>
    );
  }

  // Clearance (conditional on group - same as connections)
  if (user?.permissions?.includes('list__connections__clearance')) {
    items.push(
      <RouterSidebarItem
        key="clearance"
        to="/app/organization/clearance"
        current={isCurrentRoute('/app/organization/clearance')}
        collapsed={collapsed}
        onClick={onClick}
      >
        <i className={clsx('fal fa-fw fa-lg fa-check-double', !collapsed && 'tw:mr-1')} />
        {!collapsed && <SidebarLabel>Clearances</SidebarLabel>}
        {!collapsed && clearancesCount > 0 && <MenuBadge count={clearancesCount} />}
      </RouterSidebarItem>
    );
  }

  // Connections
  if (user?.permissions?.includes('list__connections__connectedfacility')) {
    items.push(
      <RouterSidebarItem
        key="connections"
        to="/app/organization/connections"
        current={isCurrentRoute('/app/organization/connections')}
        collapsed={collapsed}
        onClick={onClick}
      >
        <i className={clsx('fal fa-fw fa-lg fa-handshake', !collapsed && 'tw:mr-1')} />
        {!collapsed && <SidebarLabel>Connections</SidebarLabel>}
        {!collapsed && connectionsCount > 0 && <MenuBadge count={connectionsCount} />}
      </RouterSidebarItem>
    );
  }

  // Assignments
  if (user?.permissions?.includes('list__providers__providerbookingrequest')) {
    items.push(
      <RouterSidebarItem
        key="assignments"
        to="/app/healthcare-entity/booking-requests"
        current={isCurrentRoute('/app/healthcare-entity/booking-requests')}
        collapsed={collapsed}
        onClick={onClick}
      >
        <i className={clsx('fal fa-fw fa-lg fa-calendar-lines-pen', !collapsed && 'tw:mr-1')} />
        {!collapsed && <SidebarLabel>Assignments</SidebarLabel>}
        {!collapsed && <NavBarPlacementRequestsBadge />}
      </RouterSidebarItem>
    );
  }

  return items;
}

const SIDEBAR_COLLAPSED_KEY = 'prefSidebarCollapsed';

// Centralized sidebar width values - exported for use in layout calculations
export const SIDEBAR_WIDTH_COLLAPSED = '4rem';
export const SIDEBAR_WIDTH_EXPANDED = '14rem';
export const SIDEBAR_WIDTH_NONE = '0rem';

// Hook to detect if we're on desktop (lg breakpoint and above)
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    // Set initial value
    setIsDesktop(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function BrandedSideNav({
  sidebarRef,
  mobileOpen,
  onMobileClose,
}: {
  sidebarRef?: RefObject<HTMLElement>;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const location = useLocation();
  const {
    user,
    logout,
    audienceIsProvider: userIsProvider,
    audienceIsStaff: userIsStaff,
    currentEntity,
  } = useContext(SessionContext);
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;

  // Fetch menu badge counts
  const menuCounts = useMenuBadges(user);

  // Collapsed state with localStorage persistence (only on desktop)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      const ls = getLocalStorageSafe();
      const saved = ls?.getItem(SIDEBAR_COLLAPSED_KEY) ?? null;
      return saved === 'true';
    }
    return false;
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && isDesktop) {
      const ls = getLocalStorageSafe();
      try {
        ls?.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
      } catch {
        // ignore storage write errors
      }
      // Update CSS custom property for dynamic width
      // Set to 0rem if no user is logged in, otherwise use collapsed/expanded width
      const sidebarWidth = user
        ? collapsed
          ? SIDEBAR_WIDTH_COLLAPSED
          : SIDEBAR_WIDTH_EXPANDED
        : SIDEBAR_WIDTH_NONE;
      document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
    }
  }, [collapsed, isDesktop, user]);

  // Handle escape key to close mobile sidebar
  useEffect(() => {
    if (!mobileOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onMobileClose) {
        onMobileClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileOpen, onMobileClose]);

  // const pageWithoutSideNav =
  //   location.pathname.startsWith('/login') ||
  //   location.pathname.startsWith('/register') ||
  //   location.pathname.startsWith('/verify-email') ||
  //   location.pathname.startsWith('/password-reset') ||
  //   location.pathname.startsWith('/forgot-password') ||
  //   location.pathname.startsWith('/provider-reference-survey') ||
  //   location.pathname.startsWith('/app/provider/onboarding');
  //
  // // hide sidebar on these routes
  // if (pageWithoutSideNav) {
  //   return null;
  // }

  // Determine home URL using centralized logic
  const homeUrl = getHomeUrl(user, location.pathname);

  // Helper to determine if a route is current
  const isCurrentRoute = (path: string) => {
    const pathnameWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
    return path === '/'
      ? pathnameWithoutTrailingSlash === ''
      : pathnameWithoutTrailingSlash.startsWith(path);
  };

  // Handle click on sidebar item to close mobile menu
  const handleSidebarItemClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Get the appropriate sidebar items based on user type
  const sidebarItems = userIsProvider
    ? getProviderSidebarItems(
        isCurrentRoute,
        menuCounts,
        collapsed && !isMobile,
        handleSidebarItemClick
      )
    : user
      ? getOrganizationSidebarItems(
          user,
          isCurrentRoute,
          menuCounts,
          collapsed && !isMobile,
          handleSidebarItemClick
        )
      : [];

  const sidebarContent = (
    <Sidebar className={`${TRANSITION_ALL} tw:!bg-brand-black-2 tw:!border-none`}>
      <SidebarHeader className="tw:relative tw:flex tw:items-start tw:gap-2 tw:pb-0">
        <RouterLink to={homeUrl} className="tw:flex tw:items-center">
          {collapsed && !isMobile ? (
            <img alt="Sendit logo" src={LogoMarkWhiteImgSrc.toString()} className="tw:h-8" />
          ) : (
            <>
              <img alt="Sendit logo" src={LogoFullWhiteImgSrc.toString()} className="tw:h-8" />
            </>
          )}
        </RouterLink>
        {!isMobile && user && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="tw:absolute tw:top-55 tw:-translate-y-1/2 tw:right-[-12px] tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6 tw:!rounded-full tw:bg-brand-green-1 tw:text-white"
            aria-label="Collapse sidebar"
          >
            {collapsed ? (
              <i className="fas fa-angles-right tw:text-xs" />
            ) : (
              <i className="fas fa-angles-left tw:text-xs" />
            )}
          </button>
        )}
      </SidebarHeader>
      <SidebarBody>
        <SidebarSection>
          {/* TODO: remove this conditional logic once we have a proper home page for hco users */}
          {userIsStaff ||
            (userIsProvider && (
              <RouterSidebarItem
                to={homeUrl}
                current={isCurrentRoute(homeUrl)}
                collapsed={collapsed && !isMobile}
                onClick={handleSidebarItemClick}
              >
                <i
                  className={clsx(
                    'fal fa-fw fa-lg fa-house',
                    (!collapsed || isMobile) && 'tw:mr-1'
                  )}
                />
                {(!collapsed || isMobile) && <SidebarLabel>Home</SidebarLabel>}
              </RouterSidebarItem>
            ))}
          {sidebarItems}
        </SidebarSection>
        {!userIsProvider && (
          <>
            <SidebarSpacer />
            <SidebarSection>
              {(!collapsed || isMobile) && (
                <SidebarHeading className="tw:!text-zinc-300">{currentEntity}</SidebarHeading>
              )}
              {user?.permissions?.includes('list__healthcare_entities__healthcarefacility') && (
                <RouterSidebarItem
                  to="/app/organization/facilities"
                  current={isCurrentRoute('/app/organization/facilities')}
                  collapsed={collapsed && !isMobile}
                  onClick={handleSidebarItemClick}
                >
                  <i
                    className={clsx(
                      'fal fa-fw fa-lg fa-hospital',
                      (!collapsed || isMobile) && 'tw:mr-1'
                    )}
                  />
                  {(!collapsed || isMobile) && <SidebarLabel>Manage Facilities</SidebarLabel>}
                </RouterSidebarItem>
              )}
              {user?.permissions?.includes('list__healthcare_entities__healthcareentityrep') && (
                <RouterSidebarItem
                  to="/app/healthcare-entity/users"
                  current={isCurrentRoute('/app/healthcare-entity/users')}
                  collapsed={collapsed && !isMobile}
                  onClick={handleSidebarItemClick}
                >
                  <i
                    className={clsx(
                      'fal fa-fw fa-lg fa-id-badge',
                      (!collapsed || isMobile) && 'tw:mr-1'
                    )}
                  />
                  {(!collapsed || isMobile) && <SidebarLabel>Manage Users</SidebarLabel>}
                </RouterSidebarItem>
              )}
            </SidebarSection>
          </>
        )}
        {/* User menu section - shown at bottom on mobile, hidden on desktop */}
        {isMobile && user && (
          <>
            <SidebarSpacer />
            <SidebarSection>
              <div className="tw:pb-8 tw:pt-2">
                <Dropdown>
                  <DropdownButton className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:px-2 tw:py-2.5 tw:rounded-lg tw:!bg-transparent tw:!border-0 tw:!shadow-none tw:focus:!outline-none tw:before:!shadow-none tw:after:!shadow-none tw:text-white tw:hover:!bg-zinc-950/5">
                    <Avatar
                      src={user?.profile_picture}
                      initials={getInitials(`${user?.first_name} ${user?.last_name}`)}
                      className="tw:size-8 tw:bg-brand-green-1 tw:text-white"
                    />
                    <div className="tw:flex tw:flex-1 tw:flex-col tw:items-start tw:text-left">
                      <span className="tw:text-sm tw:font-medium tw:text-white">
                        {`${user?.first_name} ${user?.last_name}`}
                      </span>
                      {user?.email && (
                        <span className="tw:text-xs tw:text-zinc-300">{user.email}</span>
                      )}
                    </div>
                    <i className="fas fa-chevron-down tw:text-zinc-300 tw:ml-auto" />
                  </DropdownButton>
                  <DropdownMenu className="tw:min-w-64 tw:z-50" anchor="top start">
                    <DropdownItem
                      href="https://docs.google.com/forms/d/e/1FAIpQLSd2236EHQCrfhPqsbclegK7OCggNizrKXlIZu7xJjt-uJTydw/viewform?usp=header"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw:!no-underline tw:!text-base tw:!text-zinc-950"
                    >
                      <i className="fal fa-fw fa-lg fa-message-lines" />
                      <span>Support</span>
                    </DropdownItem>
                    <DropdownItem onClick={() => logout?.()}>
                      <i className="fal fa-fw fa-lg fa-arrow-right-from-bracket tw:mr-1" />
                      <span>Sign out</span>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </SidebarSection>
          </>
        )}
      </SidebarBody>
    </Sidebar>
  );

  // Mobile overlay mode
  if (isMobile) {
    return (
      <Headless.Dialog open={mobileOpen || false} onClose={onMobileClose || (() => {})}>
        <Headless.DialogBackdrop
          transition
          className="tw:fixed tw:inset-0 tw:bg-black/30 tw:transition tw:data-[closed]:opacity-0 tw:data-[enter]:duration-300 tw:data-[leave]:duration-200 tw:data-[enter]:ease-out tw:data-[leave]:ease-in tw:z-40"
        />
        <Headless.DialogPanel
          transition
          className="tw:fixed tw:inset-y-0 tw:left-0 tw:w-full tw:max-w-80 tw:z-50 tw:transition tw:duration-300 tw:ease-in-out tw:data-[closed]:-translate-x-full"
        >
          <div
            ref={sidebarRef as RefObject<HTMLDivElement>}
            className="tw:flex tw:min-h-0 tw:h-[100dvh] tw:flex-col tw:bg-brand-black-2 tw:shadow-xl"
          >
            {sidebarContent}
          </div>
        </Headless.DialogPanel>
      </Headless.Dialog>
    );
  }

  // Desktop mode - wrapped in fixed positioning container
  const sidebarWidth = user
    ? collapsed
      ? SIDEBAR_WIDTH_COLLAPSED
      : SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_NONE;

  return (
    <div
      className="tw:fixed tw:inset-y-0 tw:left-0 tw:min-h-screen tw:z-10 tw:hidden tw:lg:block"
      style={{ width: 'var(--sidebar-width, ' + sidebarWidth + ')' }}
    >
      <div
        ref={sidebarRef as RefObject<HTMLDivElement>}
        className={TRANSITION_ALL}
        style={{
          width: 'var(--sidebar-width, ' + sidebarWidth + ')',
        }}
      >
        {sidebarContent}
      </div>
    </div>
  );
}
